/**
 * [조회 실패 상태 블록]
 *
 * 왜 필요한가:
 *   기존 목록 화면들은 API 실패를 `console.error` 로만 처리했다.
 *   화면에는 "0건" / "등록된 회원이 없습니다" 가 그대로 남아
 *   운영자가 **조회 실패와 실제 0건을 구분할 수 없었다**.
 *   매출·구독 화면에서는 "오늘 매출 0원" 오판으로 이어질 수 있다.
 *
 * 사용처(현재): MemberDetailPage(섹션 단위) · UsagePage(탭 단위)
 *   SubscriptionsPage · MembersPage · SalesPage · DashboardPage 는 아직 `console.error` 만 한다.
 *   같은 문제가 남아 있으므로 후속 배치에서 이 컴포넌트로 옮긴다.
 *
 * 사용:
 *   const [error, setError] = useState(null);
 *   catch (err) { setError(err); }
 *   {error && <QueryError error={error} onRetry={fetchX} context="구독 목록" />}
 *
 * ── 렌더 분기 규칙 (호출부가 반드시 지킬 것) ───────────────────────
 * 1. loading    → Skeleton
 * 2. error      → <QueryError />
 * 3. 데이터 0건  → "…없습니다" 빈 상태 문구
 * 4. 그 외       → 목록
 *
 * **error 일 때 빈 상태 문구를 함께 렌더하지 않는다.** 실패를 "0건"으로 읽으면
 * 결제 분쟁 대응(회원 상세)과 정산 대사(이용 관리)에서 판단이 반대로 뒤집힌다.
 * 코드로는 `error ? null : data.length === 0 ? <빈상태/> : <목록/>` 형태가 된다.
 *
 * 401 은 여기서 다루지 않는다 — api/client.js 응답 인터셉터가 전역 로그아웃으로 처리한다.
 * 따라서 이 컴포넌트가 실제로 만나는 것은 네트워크 오류 · 5xx · 403 · 404 다.
 */
import React from 'react';
import { Alert, AlertTitle, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * axios 에러 → 화면 문구.
 *
 * 401/403 은 "다시 시도"가 의미 없다 — 재시도해도 같은 결과다.
 * 이 콘솔은 role 별 접근 가능 메뉴가 백엔드 @Roles 와 완전히 일치하지 않아
 * (예: 구독 관리 메뉴는 staff 에게도 보이지만 GET admin/subscriptions/active 는
 *  @Roles('master','manager')) 403 이 실제로 발생한다. 그때 "조회 실패"가 아니라
 * "권한 없음"이라고 말해야 운영자가 헛되이 새로고침하지 않는다.
 */
export function describeApiError(error, context = '데이터') {
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message;
  const detail = Array.isArray(serverMessage) ? serverMessage.join(' ') : serverMessage;

  if (status === 401) {
    return {
      severity: 'warning',
      title: '로그인이 만료되었습니다',
      detail: '다시 로그인한 뒤 이용해주세요.',
      retryable: false,
    };
  }
  if (status === 403) {
    return {
      severity: 'warning',
      title: `${context}을(를) 볼 권한이 없습니다`,
      detail: detail || '이 화면은 현재 계정 권한으로 조회할 수 없습니다. 필요하면 마스터 관리자에게 권한을 요청하세요.',
      retryable: false,
    };
  }
  if (status >= 500) {
    return {
      severity: 'error',
      title: `${context} 조회에 실패했습니다`,
      detail: detail || `서버 오류(${status})가 발생했습니다. 잠시 후 다시 시도해주세요.`,
      retryable: true,
    };
  }
  if (status) {
    return {
      severity: 'error',
      title: `${context} 조회에 실패했습니다`,
      detail: detail || `요청이 거부되었습니다 (HTTP ${status}).`,
      retryable: true,
    };
  }
  return {
    severity: 'error',
    title: `${context} 조회에 실패했습니다`,
    detail: '서버에 연결하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 시도해주세요.',
    retryable: true,
  };
}

const QueryError = ({ error, onRetry, context = '데이터', sx }) => {
  if (!error) return null;
  const { severity, title, detail, retryable } = describeApiError(error, context);

  return (
    <Alert
      severity={severity}
      sx={{ mb: 2, ...sx }}
      action={
        retryable && onRetry ? (
          <Button
            color="inherit"
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={onRetry}
            sx={{ whiteSpace: 'nowrap' }}
          >
            다시 시도
          </Button>
        ) : undefined
      }
    >
      <AlertTitle sx={{ fontWeight: 700, fontSize: 14 }}>{title}</AlertTitle>
      <Typography variant="body2">{detail}</Typography>
    </Alert>
  );
};

export default QueryError;
